import PropTypes from 'prop-types';
import React from 'react';
import connectToI18n from 'modules/i18n/connectToI18n';
import Button from 'components/Button.jsx';
import Box from 'components/Box.jsx';
import BoxSection from 'components/BoxSection.jsx';
import {
    AddressInputGroup,
    CheckboxGroup,
    DateInputGroup,
    FormSelectGroup,
    TextInputGroup,
} from 'components/ui/form';
import Heading from 'components/Heading.jsx';
import Utils from 'modules/utils';
const { linkTo } = Utils;
import LoadingAnimation from 'components/LoadingAnimation.jsx';
import { INITIAL_LOADING, SAVING, propType as statusPropType } from 'modules/utils/ajaxStatuses';
import Wrapper from 'components/Wrapper.jsx';

export class PaymentProfileCompletion extends React.Component {
    static propTypes = {
        backLink: PropTypes.node,
        countries: PropTypes.array,
        formData: PropTypes.object.isRequired,
        edit: PropTypes.func.isRequired,
        submit: PropTypes.func.isRequired,
        clientErrors: PropTypes.object.isRequired,
        serverErrors: PropTypes.object.isRequired,
        showErrors: PropTypes.bool.isRequired,
        i18n: PropTypes.string.isRequired,
        trans: PropTypes.func.isRequired,
        status: statusPropType,
    };

    onChange = (name, value) => {
        this.props.edit(name, value);
    };

    onSubmit = e => {
        e.preventDefault();
        this.props.submit();
    };

    render() {
        const {
            backLink,
            formData,
            clientErrors,
            serverErrors,
            showErrors,
            countries,
            trans,
            status,
            i18n,
        } = this.props;

        if (status === INITIAL_LOADING) {
            return <LoadingAnimation type="spinner" />;
        }

        return (
            <Wrapper size="small" margin="both" xsPadding>
                <Box>
                    <BoxSection>
                        <Heading size={1} className="paymentProfileCompletion-title">
                            {trans('payment.profile.title')}
                        </Heading>
                        <p className="paymentProfileCompletion-text u-mb-4">
                            {trans('payment.profile.explanationText')}
                            <a
                                className="paymentProfileCompletion-knowMore"
                                href={linkTo('support/kyc-information')}
                                target="_blank"
                                data-bypass
                            >
                                <span className="icon icon-info-sign" />
                                <span>{trans('payment.profile.knowMore')}</span>
                            </a>
                        </p>
                    </BoxSection>
                    <BoxSection>
                        <form>
                            <div className="paymentProfileCompletion-formRow">
                                <TextInputGroup
                                    className="paymentProfileCompletion-formCell"
                                    name="phone"
                                    inputVariant="inverse"
                                    label={trans('global.phoneNumber')}
                                    onChange={this.onChange}
                                    value={formData.phone}
                                    showErrors={showErrors}
                                    clientErrors={clientErrors.phone}
                                    serverErrors={serverErrors.phone}
                                />
                            </div>
                            <div className="paymentProfileCompletion-formRow">
                                <AddressInputGroup
                                    cityName="cityId"
                                    initialCountry={formData.countryId}
                                    initialZip={formData.zipCode}
                                    onChange={this.onChange}
                                >
                                    {({ zip, cities, onZipChange, areCitiesReady }) => (
                                        <React.Fragment>
                                            <TextInputGroup
                                                className="paymentProfileCompletion-formCell"
                                                name="street"
                                                label={trans('address.numberWay')}
                                                value={formData.street}
                                                onChange={this.onChange}
                                                inputVariant="inverse"
                                                showErrors={showErrors}
                                                clientErrors={clientErrors.street}
                                                serverErrors={
                                                    serverErrors.address &&
                                                    serverErrors.address.street
                                                }
                                            />
                                            <div className="paymentProfileCompletion-formCell u-flexible-row">
                                                <TextInputGroup
                                                    className="paymentProfileCompletion-zipCode"
                                                    name="zipCode"
                                                    inputVariant="inverse"
                                                    label={trans('address.zip')}
                                                    value={zip}
                                                    onChange={onZipChange}
                                                    showErrors={showErrors}
                                                    clientErrors={clientErrors.zipCode}
                                                    serverErrors={serverErrors.zipCode}
                                                />
                                                <FormSelectGroup
                                                    className="paymentProfileCompletion-city"
                                                    name="cityId"
                                                    clientErrors={clientErrors.cityId}
                                                    serverErrors={
                                                        serverErrors.address &&
                                                        serverErrors.address.city
                                                    }
                                                    showErrors={showErrors}
                                                    isDisabled={!areCitiesReady}
                                                    emptyOption={
                                                        cities.length !== 0
                                                            ? trans('address.cityDefault')
                                                            : trans('user.city.void')
                                                    }
                                                    inputVariant="inverse"
                                                    label={trans('address.city')}
                                                    options={cities}
                                                    value={formData.cityId}
                                                    onChange={this.onChange}
                                                />
                                            </div>
                                        </React.Fragment>
                                    )}
                                </AddressInputGroup>
                            </div>
                            <div className="paymentProfileCompletion-formRow">
                                <DateInputGroup
                                    className="paymentProfileCompletion-formCell"
                                    name="birthday"
                                    inputVariant="inverse"
                                    label={trans('global.dateOfBirth')}
                                    clientErrors={clientErrors.birthday}
                                    serverErrors={serverErrors.birthday}
                                    showErrors={showErrors}
                                    value={formData.birthday}
                                    onChange={this.onChange}
                                />
                                <FormSelectGroup
                                    className="paymentProfileCompletion-formCell"
                                    name="nationality"
                                    inputVariant="inverse"
                                    clientErrors={clientErrors.nationality}
                                    serverErrors={serverErrors.nationality}
                                    showErrors={showErrors}
                                    label={trans('global.nationality')}
                                    emptyOption={trans('address.countryChoose')}
                                    options={countries.map(country => ({
                                        label: country.name,
                                        value: country.code,
                                    }))}
                                    value={formData.nationality}
                                    onChange={this.onChange}
                                />
                            </div>
                            <div className="paymentProfileCompletion-formRow">
                                <CheckboxGroup
                                    clientErrors={clientErrors.hasAcceptedTOS}
                                    id="hasAcceptedTOS"
                                    inverse
                                    isChecked={formData.hasAcceptedTOS}
                                    onChange={this.onChange}
                                    showErrors={showErrors}
                                    serverErrors={serverErrors.hasAcceptedTOS}
                                >
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: trans('user.tos.mangoPay', {
                                                '%url%': `/${i18n}/p/terms`,
                                            }),
                                        }}
                                    />
                                </CheckboxGroup>
                            </div>
                            <div className="row">
                                <div className="col-md-4 col-md-offset-8">
                                    <Button
                                        variant="success"
                                        block
                                        loading={status === SAVING}
                                        onClick={this.onSubmit}
                                    >
                                        {trans('global.continue')}
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </BoxSection>
                </Box>
                {backLink}
            </Wrapper>
        );
    }
}

export default connectToI18n(PaymentProfileCompletion);
